/**
 * Rating Helpers - Shared utility functions
 *
 * SYNC: This module is shared between frontend and backend.
 */

import { THRESHOLDS } from './thresholds.mjs';
import { COMPASS_POINTS, compassToDegrees } from '../direction.mjs';

/**
 * Smallest angular distance between two bearings in degrees (0-180)
 */
function angularDistance(a, b) {
    const diff = Math.abs(a - b) % 360;
    return diff > 180 ? 360 - diff : diff;
}

/**
 * Determine whether the wind is offshore for a given spot.
 *
 * Offshore depends on which way the beach faces, so it must be derived
 * per spot — a fixed list is only correct for west-facing coasts:
 * 1. ideal_wind (curated per spot) — offshore if within ±45°
 * 2. swell window — offshore if within ±67.5° of the direction opposite
 *    the window's circular midpoint (the window is where swell arrives
 *    from, i.e. the direction the beach faces)
 * 3. no spot config — legacy east-quadrant list (west-coast assumption)
 *
 * @param {string} windDir - Current wind direction (compass point, e.g. 'NE')
 * @param {Object|null} spotConfig - Spot config with ideal_wind / swell_window_min/max
 * @returns {boolean}
 */
export function isOffshoreWind(windDir, spotConfig) {
    // compassToDegrees returns 0 (North) for unknown strings, so validate
    // membership before converting
    if (!windDir || !COMPASS_POINTS.includes(windDir)) return false;

    const windDeg = compassToDegrees(windDir);

    const idealWind = spotConfig?.ideal_wind;
    if (idealWind && idealWind !== 'calm' && COMPASS_POINTS.includes(idealWind)) {
        return angularDistance(windDeg, compassToDegrees(idealWind)) <= 45;
    }

    const winMin = spotConfig?.swell_window_min;
    const winMax = spotConfig?.swell_window_max;
    if (typeof winMin === 'number' && typeof winMax === 'number') {
        const midpoint = winMin <= winMax
            ? (winMin + winMax) / 2
            : ((winMin + winMax + 360) / 2) % 360;
        const offshoreCenter = (midpoint + 180) % 360;
        return angularDistance(windDeg, offshoreCenter) <= 67.5;
    }

    return ['E', 'ENE', 'NE', 'ESE'].includes(windDir);
}

/**
 * Parse wind speed from string like "5 mph" or "10 to 15 mph"
 * @param {string} windStr - Wind speed string
 * @returns {number|null}
 */
export function parseWindSpeed(windStr) {
    if (!windStr || typeof windStr !== 'string') return null;
    const match = windStr.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
}

/**
 * Check if a swell direction is within a spot's swell window
 * Handles wrap-around (e.g., window 270-45 degrees)
 * @param {number|null} direction - Swell direction in degrees
 * @param {number} windowMin - Minimum window angle
 * @param {number} windowMax - Maximum window angle
 * @returns {boolean}
 */
export function isInSwellWindow(direction, windowMin, windowMax) {
    if (direction === null || direction === undefined) return true; // Assume in-window if unknown
    if (windowMin === undefined || windowMax === undefined) return true; // No window defined

    // Normalize direction to 0-360
    direction = ((direction % 360) + 360) % 360;

    if (windowMin <= windowMax) {
        // Normal case: window doesn't wrap around (e.g., 180 to 290)
        return direction >= windowMin && direction <= windowMax;
    } else {
        // Wrap-around case: window crosses 0 degrees (e.g., 270 to 45)
        return direction >= windowMin || direction <= windowMax;
    }
}

/**
 * Check if wind direction matches spot's ideal offshore direction
 * @param {string} windDir - Current wind direction (compass point)
 * @param {string} idealWind - Ideal wind direction (compass point)
 * @returns {boolean}
 */
export function matchesIdealWind(windDir, idealWind) {
    if (!windDir || !idealWind) return false;
    // Direct match or within one compass point
    const windIndex = COMPASS_POINTS.indexOf(windDir);
    const idealIndex = COMPASS_POINTS.indexOf(idealWind);
    if (windIndex === -1 || idealIndex === -1) return windDir === idealWind;
    // Within 1 compass point (22.5 degrees)
    const diff = Math.abs(windIndex - idealIndex);
    return diff <= 1 || diff >= 15; // Handle wrap-around
}

/**
 * Check if current tide matches spot's ideal tide
 * @param {number|object} waterLevel - Water level value or object with value property
 * @param {string} idealTide - Ideal tide ('all', 'low', 'mid', 'high')
 * @returns {boolean}
 */
export function matchesIdealTide(waterLevel, idealTide) {
    if (waterLevel === null || !idealTide) return false;
    const level = typeof waterLevel === 'object' ? waterLevel.value : waterLevel;

    if (idealTide === 'all') return true;
    if (idealTide === 'low') return level < THRESHOLDS.tide.low;
    if (idealTide === 'mid') return level >= THRESHOLDS.tide.low && level <= THRESHOLDS.tide.high;
    if (idealTide === 'high') return level > THRESHOLDS.tide.high;
    return false;
}

/**
 * Check if wave height is within spot's ideal range
 * @param {number|null} waveHeight - Wave height in feet
 * @param {number} idealMin - Minimum ideal height
 * @param {number} idealMax - Maximum ideal height
 * @returns {boolean}
 */
export function isInIdealSwellRange(waveHeight, idealMin, idealMax) {
    if (waveHeight === null || idealMin === undefined || idealMax === undefined) return false;
    return waveHeight >= idealMin && waveHeight <= idealMax;
}

/**
 * Get wave height coefficient from spot config
 * The coefficient adjusts buoy readings to actual wave size at the spot
 * e.g., 1.3 = waves are 30% larger than buoy reading (like The Wedge)
 * e.g., 0.7 = waves are 30% smaller than buoy reading (sheltered spots)
 * @param {object|null} spotConfig - Spot configuration
 * @returns {number}
 */
export function getWaveCoefficient(spotConfig) {
    if (!spotConfig || !spotConfig.buoys || spotConfig.buoys.length === 0) {
        return 1.0; // Default: no adjustment
    }
    // Use the coefficient from the primary buoy (first in array)
    return spotConfig.buoys[0].coefficient || 1.0;
}
